import useGetMyRegionPreferences from '@/api/queries/me/useGetMyRegionPreferences';
import {
  gqlInvoiceStatusToInvoiceStatusMap,
  Invoice,
} from '@/api/queries/project/useGetProjectInvoices';
import { Alert, Badge, Card, Divider } from '@payaca/ambrosia';
import CurrencyValue from '@payaca/components/currencyValue/CurrencyValue';

import {
  DateFormats,
  getInternationalDateFormatByLocale,
} from '@payaca/helpers/internationalHelper';
import { checkUnreachable } from '@payaca/utilities/guards';
import { format } from 'date-fns';
import { FC } from 'react';
import { InvoiceCardActions } from './InvoiceCardActions';
import { InvoiceCard as _InvoiceCard } from './components/InvoiceCard';

const getBadgePropsFromInvoiceStatus = (status: Invoice['status']) => {
  switch (status) {
    case 'DRAFT':
      return { colour: 'gray' as const };
    case 'SENT':
      return { colour: 'blue' as const };
    case 'PAID':
      return { colour: 'green' as const };
    case 'VOID':
      return { colour: 'red' as const };
    case 'PARTIALLY_PAID':
      return { colour: 'yellow' as const };
    default:
      checkUnreachable(status);
  }
};

export const InvoiceCard: FC<{
  invoice: Invoice;
  onRecordPaymentSuccess?: () => void;
  /** Hide the card's border and shadow, e.g. when nested inside another card. */
  borderless?: boolean;
}> = ({ invoice, onRecordPaymentSuccess, borderless }) => {
  const issue = invoice.bouncedAt ? 'E-mail has bounced' : null;
  const { data: accountRegionPreferences } = useGetMyRegionPreferences();

  const card = (
    <_InvoiceCard borderless={borderless}>
      <_InvoiceCard.Grid>
        <_InvoiceCard.Grid.Element>
          <h3>Invoice #{invoice.customReference || invoice.reference}</h3>
        </_InvoiceCard.Grid.Element>
        <_InvoiceCard.Grid.Element title={'Total amount:'}>
          <p>
            <CurrencyValue {...invoice.totalValue} />
          </p>
        </_InvoiceCard.Grid.Element>
        <_InvoiceCard.Grid.Element title={'Outstanding:'}>
          <p>
            <CurrencyValue {...invoice.outstandingValue} />
          </p>
        </_InvoiceCard.Grid.Element>
        <_InvoiceCard.Grid.Element title={'Issued:'}>
          <p>
            {invoice.sentAt ? (
              <time dateTime={invoice.sentAt}>
                {format(
                  invoice.sentAt,
                  getInternationalDateFormatByLocale(
                    DateFormats.SHORT,
                    accountRegionPreferences?.locale
                  )
                )}
              </time>
            ) : (
              '-'
            )}
          </p>
        </_InvoiceCard.Grid.Element>
        <_InvoiceCard.Grid.Element title={'Due:'}>
          <p>
            {invoice.dueAt ? (
              <time dateTime={invoice.dueAt}>
                {format(
                  invoice.dueAt,
                  getInternationalDateFormatByLocale(
                    DateFormats.SHORT,
                    accountRegionPreferences?.locale
                  )
                )}
              </time>
            ) : (
              '-'
            )}
          </p>
        </_InvoiceCard.Grid.Element>
        <_InvoiceCard.Grid.Element>
          <div className="flex space-x-1">
            <Badge
              variant="soft"
              {...getBadgePropsFromInvoiceStatus(invoice.status)}
            >
              {gqlInvoiceStatusToInvoiceStatusMap[invoice.status]}
            </Badge>
            {invoice.outstandingValue.value > 0 &&
              invoice.sentAt &&
              invoice.dueAt &&
              new Date(invoice.dueAt) < new Date() && (
                <Badge variant="soft" colour="red">
                  Overdue
                </Badge>
              )}
          </div>
        </_InvoiceCard.Grid.Element>
      </_InvoiceCard.Grid>
      {issue && <Alert colour="yellow">E-mail has bounced</Alert>}
      <Divider />
      <InvoiceCardActions
        invoice={invoice}
        onRecordPaymentSuccess={onRecordPaymentSuccess}
      />
    </_InvoiceCard>
  );

  if (invoice.status === 'DRAFT') {
    return (
      <Card sizeVariant="xs" className="bg-gray-50">
        <Card.Body className="space-y-1">
          <span className="ml-2">Draft</span>
          {card}
        </Card.Body>
      </Card>
    );
  }

  return card;
};
